import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import type { OrderDetailsOrderLine } from '@/types/orderDetailsOrder';

import { Typography } from '@/components/Typography';
import type { OrderAppStorefrontWithdrawalEligibilityQuery } from '@/graphql/orderApp/api';
import { WITHDRAWAL_ENABLED } from '@/lib/const';
import { localizedWithdrawalEligibilityErrorMessage } from '@/lib/withdrawal/withdrawalEligibilityErrorMessage';
import { hasWithdrawalActionableLines } from '@/lib/withdrawal/withdrawalExcludedProductTypes';
import type { OrderDetailsGuestSession } from '@/views/OrderDetailsView/helpers';

import { useGuestWithdrawalEligibility } from './useGuestWithdrawalEligibility';
import { useOrderWithdrawalUiState } from './useOrderWithdrawalUiState';

export type UseOrderDetailsWithdrawalParams = {
  formatMessage: IntlShape['formatMessage'];
  guestSession: OrderDetailsGuestSession | undefined;
  hasFulfillment: boolean;
  isOrderInWithdrawalScope: boolean;
  orderLines: OrderDetailsOrderLine[];
  type: string | undefined;
  withdrawalEligibilityData:
    | OrderAppStorefrontWithdrawalEligibilityQuery
    | undefined;
  withdrawalEligibilityQueryLoading: boolean;
};

export function useOrderDetailsWithdrawal({
  formatMessage,
  guestSession,
  hasFulfillment,
  isOrderInWithdrawalScope,
  orderLines,
  type,
  withdrawalEligibilityData,
  withdrawalEligibilityQueryLoading,
}: UseOrderDetailsWithdrawalParams) {
  const withdrawalActive = WITHDRAWAL_ENABLED && isOrderInWithdrawalScope;
  const hideWithdrawalSection = !hasWithdrawalActionableLines(orderLines);
  const guestWithdrawalEnabled =
    withdrawalActive && Boolean(guestSession) && !hideWithdrawalSection;

  const {
    guestEligibilityCaptchaKey,
    guestEligibilityFetchFailed,
    guestEligibilityLocal,
    guestHasCheckedEligibility,
    handleRecaptchaChange,
    handleRecaptchaExpired,
  } = useGuestWithdrawalEligibility(guestSession, guestWithdrawalEnabled);

  const {
    isGuestEligibilityRecaptchaVisible,
    isWithdrawalEligibilityLoading,
    showGlobalOrderNotEligibleMessage,
    showGuestReturnButton,
    showWithdrawalCta,
    withdrawalBlockedOnlyByOutOfEuScope,
    withdrawalCode,
    withdrawalEligibility,
    withdrawalEligibilityResolvedForUi,
  } = useOrderWithdrawalUiState({
    guestEligibilityFetchFailed,
    guestEligibilityLocal,
    guestHasCheckedEligibility,
    guestSession,
    hasFulfillment,
    isOrderInWithdrawalScope: withdrawalActive,
    orderLines,
    type,
    withdrawalEligibilityData,
    withdrawalEligibilityQueryLoading,
  });

  const eligibilityErrorMessage = localizedWithdrawalEligibilityErrorMessage(
    formatMessage,
    {
      isGuestOrderLookup: Boolean(guestSession),
      withdrawalCode,
    },
  );

  const orderNotEligibleGlobalMessage: ReactNode =
    showGlobalOrderNotEligibleMessage && eligibilityErrorMessage ? (
      <Typography
        color='secondaryGrey'
        data-testid='OrderDetailsViewOrderNotEligibleGlobal'
        marginBottom={2}
        scale='p3'
        textAlign='center'
      >
        {eligibilityErrorMessage}
      </Typography>
    ) : null;

  const showOrderHistoryWithdrawalSection =
    withdrawalActive &&
    type === 'ORDERHISTORY' &&
    !hasFulfillment &&
    !(withdrawalBlockedOnlyByOutOfEuScope && !guestSession) &&
    !hideWithdrawalSection;

  return {
    guestEligibilityCaptchaKey,
    guestEligibilityFetchFailed,
    handleRecaptchaChange,
    handleRecaptchaExpired,
    isGuestEligibilityRecaptchaVisible,
    isWithdrawalEligibilityLoading,
    orderNotEligibleGlobalMessage,
    showGuestReturnButton,
    showOrderHistoryWithdrawalSection,
    showWithdrawalCta,
    withdrawalBlockedOnlyByOutOfEuScope,
    withdrawalCode,
    withdrawalEligibility,
    withdrawalEligibilityResolvedForUi,
  };
}

export type OrderDetailsWithdrawalUiState = ReturnType<
  typeof useOrderDetailsWithdrawal
>;
